import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useCheckoutDetails} from "../hooks/useCheckoutDetails";
import {useStateContext} from "../contexts/ContextProvider";
import {createPaypalPayment, createStripeCheckoutSession, createMolliePayment} from "../api/apiClient";
import PaymentConfirmation from "../components/PaymentConfirmation";
import {trackInitiateCheckout} from "../utils/fbPixel";
import {BOOKING_ERROR_MESSAGES, INVALID_COUPON_ERROR_CODE} from "../utility/bookingErrorMessages";
import {toast} from "react-toastify";
import {useUnattendedAccessTerms} from "../hooks/useUnattendedAccessTerms";
import UnattendedAccessTermsModal from "../components/UnattendedAccessTermsModal";
import CheckoutSteps from "../components/CheckoutSteps";
import CouponCodeField from "../components/CouponCodeField";
import {isPayByBankEnabled, isMollieEnabled} from "../utility/featureFlags";
import {getCouponPaymentMethod} from "../utility/couponPaymentMethod";
import {isProductSoldOut, PRODUCT_SOLD_OUT_ERROR_CODE, PRODUCT_SOLD_OUT_LABEL} from "../utility/productAvailability";

const CheckoutPage = () => {
    const {token, user, setUser, setToken} = useStateContext();
    const {eventUuid, productUuid} = useParams();
    const [coupon, setCoupon] = useState(null);
    const [reloadCheckout, setReloadCheckout] = useState(0);
    // Preselect Pay by Bank, but only when its block is actually rendered - otherwise the buy
    // button would be enabled for a method the customer never got to see.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        () => (isMollieEnabled() || isPayByBankEnabled()) ? "paybybank" : null
    );
    const {event, product, productActivationDate, productExpirationDate, bruttoPriceWithCoupon, couponCode, couponErrorMessage} = useCheckoutDetails(eventUuid, productUuid, coupon, getCouponPaymentMethod(selectedPaymentMethod), reloadCheckout);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentProvider, setPaymentProvider] = useState(null);
    const navigate = useNavigate();
    const unattendedAccessTerms = useUnattendedAccessTerms();

    useEffect(() => {
        if (!token) {
            navigate("/login");
        }

        window.scrollTo(0, 0);
    }, [navigate, token]);

    // Track InitiateCheckout event when page loads
    useEffect(() => {
        trackInitiateCheckout();
    }, []);

    const productSoldOut = isProductSoldOut(product);
    const isButtonDisabled = !selectedPaymentMethod || !agreeTerms || processing || productSoldOut;

    if (!event || !product) {
        return <div>Loading...</div>;
    }

    const handlePaymentMethodChange = (method) => {
        setSelectedPaymentMethod(method);
    };

    const handlePayByBankResponse = (response) => {
        if (response?.status === "confirmed") {
            // Payment was successful with coupon covering full amount
            setPaymentProvider("Pay by Bank");
            setPaymentStatus("success");
            return;
        }

        // Mollie answers with checkoutUrl, Stripe with url.
        const redirectUrl = response?.checkoutUrl || response?.url;

        if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
        }

        toast.error("Fehler bei der Zahlung. Bitte versuche es erneut.");
        setProcessing(false);
    };

    const handlePayByBankError = (error) => {
        console.error("Pay by Bank payment failed:", error);

        let errorData;
        try {
            errorData = JSON.parse(error.message);
        } catch (parseError) {
            errorData = null;
        }

        if (errorData?.code === INVALID_COUPON_ERROR_CODE) {
            // Reload the checkout details so the real price is shown.
            setReloadCheckout((prev) => prev + 1);
            setProcessing(false);
            return;
        }

        if (errorData?.code === PRODUCT_SOLD_OUT_ERROR_CODE) {
            // The last unit was taken between page load and payment: refresh the
            // product so the sold out state is shown instead of a generic error.
            toast.error(BOOKING_ERROR_MESSAGES[PRODUCT_SOLD_OUT_ERROR_CODE]);
            setReloadCheckout((prev) => prev + 1);
            setProcessing(false);
            return;
        }

        if (errorData?.code === 808) {
            unattendedAccessTerms.promptForTerms(handlePayment);
            setProcessing(false);
            return;
        }

        toast.error(BOOKING_ERROR_MESSAGES[errorData?.code] || "Fehler bei der Zahlung. Bitte versuche es erneut.");
        setProcessing(false);
    };

    const handlePayment = async () => {
        setProcessing(true);
        if (selectedPaymentMethod === "paypal") {
            try {
                const response = await createPaypalPayment(eventUuid, productUuid, couponCode);
                
                if (response?.status === "confirmed") {
                    // Payment was successful with coupon covering full amount
                    setPaymentProvider("PayPal");
                    setPaymentStatus("success");
                } else if (response?.status === "created" && response?.approvalUrl) {
                    // Regular flow - redirect to PayPal
                    window.location.href = response.approvalUrl;
                } else {
                    toast.error("Fehler bei der PayPal-Zahlung. Bitte versuche es erneut.");
                    setProcessing(false);
                }
            }
            catch (error) {
                console.log('PayPal payment error:', error);
                
                // Parse the error message which is a stringified JSON
                let errorData;
                try {
                    errorData = JSON.parse(error.message);
                    console.log('Parsed error data:', errorData);
                    
                    if (errorData.code === INVALID_COUPON_ERROR_CODE) {
                        console.log('Invalid coupon code detected, reloading checkout details');
                        setReloadCheckout((prev) => prev + 1);
                        setProcessing(false);
                        return; // Exit early to avoid showing alert
                    }

                    if (errorData.code === PRODUCT_SOLD_OUT_ERROR_CODE) {
                        // The last unit was taken between page load and payment: refresh the
                        // product so the sold out state is shown instead of a generic error.
                        toast.error(BOOKING_ERROR_MESSAGES[PRODUCT_SOLD_OUT_ERROR_CODE]);
                        setReloadCheckout((prev) => prev + 1);
                        setProcessing(false);
                        return;
                    }

                    if (errorData.code === 808) {
                        unattendedAccessTerms.promptForTerms(handlePayment);
                        setProcessing(false);
                        return;
                    }

                    const bookingErrorMessage = BOOKING_ERROR_MESSAGES[errorData.code];
                    if (bookingErrorMessage) {
                        toast.error(bookingErrorMessage);
                        setProcessing(false);
                        return;
                    }
                } catch (parseError) {
                    console.log('Error parsing error message:', parseError);
                    // If parsing fails, continue with default error handling
                }

                toast.error("Fehler bei der PayPal-Zahlung. Bitte versuche es erneut.");
                setProcessing(false);
            }
        }
        else if (selectedPaymentMethod === "creditcard") {
            createStripeCheckoutSession(eventUuid, productUuid, couponCode, 'card')
                .then(async (response) => {
                    if (response?.status === "confirmed") {
                        // Payment was successful with coupon covering full amount
                        setPaymentProvider("Stripe");
                        setPaymentStatus("success");
                    } else if (response?.url) {
                        // Regular flow - redirect to Stripe
                        window.location.href = response.url;
                    } else {
                        toast.error("Fehler bei der Zahlung. Bitte versuche es erneut.");
                        setProcessing(false);
                    }
                })
                .catch((error) => {
                    console.error("Stripe payment failed:", error);
                    
                    // Parse the error message which is a stringified JSON
                    let errorData;
                    try {
                        errorData = JSON.parse(error.message);
                        console.log('Parsed error data:', errorData);
                        
                        if (errorData.code === INVALID_COUPON_ERROR_CODE) {
                            console.log('Invalid coupon code detected, reloading checkout details');
                            setReloadCheckout((prev) => prev + 1);
                            setProcessing(false);
                            return; // Exit early to avoid showing alert
                        }

                        if (errorData.code === PRODUCT_SOLD_OUT_ERROR_CODE) {
                            // The last unit was taken between page load and payment: refresh the
                            // product so the sold out state is shown instead of a generic error.
                            toast.error(BOOKING_ERROR_MESSAGES[PRODUCT_SOLD_OUT_ERROR_CODE]);
                            setReloadCheckout((prev) => prev + 1);
                            setProcessing(false);
                            return;
                        }

                        if (errorData.code === 808) {
                            unattendedAccessTerms.promptForTerms(handlePayment);
                            setProcessing(false);
                            return;
                        }

                        const bookingErrorMessage = BOOKING_ERROR_MESSAGES[errorData.code];
                        if (bookingErrorMessage) {
                            toast.error(bookingErrorMessage);
                            setProcessing(false);
                            return;
                        }
                    } catch (parseError) {
                        console.log('Error parsing error message:', parseError);
                        // If parsing fails, continue with default error handling
                    }

                    toast.error("Fehler bei der Zahlung. Bitte versuche es erneut.");
                    setProcessing(false);
                });
        }
        else if (selectedPaymentMethod === "paybybank") {
            // Pay by Bank is served by Mollie; the Stripe branch stays behind its own flag
            // until Stripe enables Pay by Bank for Germany.
            const payByBankRequest = isMollieEnabled()
                ? createMolliePayment(eventUuid, productUuid, couponCode)
                : createStripeCheckoutSession(eventUuid, productUuid, couponCode, 'pay_by_bank');

            payByBankRequest
                .then(handlePayByBankResponse)
                .catch(handlePayByBankError);
        }
        else if (selectedPaymentMethod === "sepa") {
            createStripeCheckoutSession(eventUuid, productUuid, couponCode, 'sepa_debit')
                .then(async (response) => {
                    if (response?.status === "confirmed") {
                        // Payment was successful with coupon covering full amount
                        setPaymentProvider("Stripe");
                        setPaymentStatus("success");
                    } else if (response?.url) {
                        // Regular flow - redirect to Stripe
                        window.location.href = response.url;
                    } else {
                        toast.error("Fehler bei der Zahlung. Bitte versuche es erneut.");
                        setProcessing(false);
                    }
                })
                .catch((error) => {
                    console.error("Stripe payment failed:", error);
                    
                    // Parse the error message which is a stringified JSON
                    let errorData;
                    try {
                        errorData = JSON.parse(error.message);
                        console.log('Parsed error data:', errorData);
                        
                        if (errorData.code === INVALID_COUPON_ERROR_CODE) {
                            console.log('Invalid coupon code detected, reloading checkout details');
                            setReloadCheckout((prev) => prev + 1);
                            setProcessing(false);
                            return; // Exit early to avoid showing alert
                        }

                        if (errorData.code === PRODUCT_SOLD_OUT_ERROR_CODE) {
                            // The last unit was taken between page load and payment: refresh the
                            // product so the sold out state is shown instead of a generic error.
                            toast.error(BOOKING_ERROR_MESSAGES[PRODUCT_SOLD_OUT_ERROR_CODE]);
                            setReloadCheckout((prev) => prev + 1);
                            setProcessing(false);
                            return;
                        }

                        if (errorData.code === 808) {
                            unattendedAccessTerms.promptForTerms(handlePayment);
                            setProcessing(false);
                            return;
                        }

                        const bookingErrorMessage = BOOKING_ERROR_MESSAGES[errorData.code];
                        if (bookingErrorMessage) {
                            toast.error(bookingErrorMessage);
                            setProcessing(false);
                            return;
                        }
                    } catch (parseError) {
                        console.log('Error parsing error message:', parseError);
                        // If parsing fails, continue with default error handling
                    }

                    toast.error("Fehler bei der Zahlung. Bitte versuche es erneut.");
                    setProcessing(false);
                });
        }
        else {
            setProcessing(false);
            toast.error("Bitte wähle eine Zahlungsmethode aus.");
        }
    };

    // If payment is successful with coupon covering full amount, show payment confirmation
    if (paymentStatus === "success" && paymentProvider) {
        return <PaymentConfirmation paymentStatus={paymentStatus} paymentProvider={paymentProvider} />;
    }

    return (
        <div className="container mx-auto max-w-6xl p-6 bg-white rounded-lg shadow-md">
            {/* Back Link */}
            <div className="mb-6">
                <button
                    className="text-pegasusPurple hover:text-pink-700 font-medium"
                    onClick={() => navigate(-1)}
                >
                    &lt; Zurück
                </button>
            </div>

            <div className="mb-8">
                <CheckoutSteps currentStep={2}/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Column */}
                <div className="col-span-2">
                    {/* Your Data */}
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Deine Daten</h2>
                    <div className="flex justify-between items-center mb-6">
                        <p className="text-gray-700">
                            Willkommen <span className="font-semibold">{user?.firstName || "Gast"}</span>
                        </p>
                        <button
                            className="text-pegasusPurple underline"
                            onClick={() => {
                                setUser(null);
                                setToken(null);
                                navigate("/login");
                                return;
                            }}
                        >
                            Nicht du?
                        </button>
                    </div>

                    {/* Payment Methods */}
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Kasse</h2>
                    <div className="space-y-4">

                        {/* Pay by Bank */}
                        {(isMollieEnabled() || isPayByBankEnabled()) && (
                            <div
                                className={`border p-4 rounded-lg ${selectedPaymentMethod === "paybybank" ? "border-pegasusPurple" : "border-gray-300"
                                }`}
                            >
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="paybybank"
                                        className="mr-4"
                                        checked={selectedPaymentMethod === "paybybank"}
                                        onChange={() => handlePaymentMethodChange("paybybank")}
                                    />
                                    <span>💚 Pay by Bank</span>
                                    <span className="ml-2 text-xs font-semibold text-pegasusPurple bg-pink-100 px-2 py-0.5 rounded-full">
                                        EMPFOHLEN
                                    </span>
                                </label>
                                <p className="mt-2 text-gray-600 text-sm">
                                    Direkt und sicher über deine Bank-App bezahlen – so unterstützt du unser Studio mit den geringsten Gebühren.
                                </p>
                                {isMollieEnabled() && (
                                    <p className="mt-1 text-gray-500 text-xs">
                                        In deiner Banking-App erscheint{" "}
                                        <span className="font-semibold text-gray-700">Stichting Mollie Payments</span>, unser Zahlungspartner.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* SEPA */}
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "sepa" ? "border-pegasusPurple" : "border-gray-300"
                            }`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="sepa"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "sepa"}
                                    onChange={() => handlePaymentMethodChange("sepa")}
                                />
                                <span>SEPA Lastschrift</span>
                            </label>
                            {selectedPaymentMethod === "sepa" && (
                                <p className="mt-2 text-gray-600 text-sm">
                                    Zahlung mit SEPA Lastschrift. Im nächsten Schritt wirst Du zu Stripe
                                    umgeleitet.
                                </p>
                            )}
                        </div>

                        {/* Credit Card */}
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "creditcard" ? "border-pegasusPurple" : "border-gray-300"
                            }`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="creditcard"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "creditcard"}
                                    onChange={() => handlePaymentMethodChange("creditcard")}
                                />
                                <span>Kreditkarte</span>
                            </label>
                            {selectedPaymentMethod === "creditcard" && (
                                <p className="mt-2 text-gray-600 text-sm">
                                    Zahlung mit Kreditkarte. Im nächsten Schritt wirst Du zu Stripe
                                    umgeleitet.
                                </p>
                            )}
                        </div>

                        {/* PayPal */}
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "paypal" ? "border-pegasusPurple" : "border-gray-300"
                            }`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="paypal"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "paypal"}
                                    onChange={() => handlePaymentMethodChange("paypal")}
                                />
                                <span>PayPal</span>
                            </label>
                            {selectedPaymentMethod === "paypal" && (
                                <p className="mt-2 text-gray-600 text-sm">
                                    Zahlung mit PayPal. Im nächsten Schritt wirst Du zu PayPal
                                    umgeleitet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div>
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Deine Bestellung</h2>
                    <div className="border p-4 rounded-lg shadow-md">
                        <img
                            src={event.image}
                            alt={event.name}
                            className="w-full h-32 object-cover rounded-md mb-4"
                        />
                        <h3 className="font-bold text-gray-800">{event.name}</h3>
                        <p className="text-sm text-gray-600">
                            {new Date(event.eventDate).toLocaleDateString("de-DE", {
                                weekday : "long",
                                day     : "2-digit",
                                month   : "long",
                                year    : "numeric",
                            })}{" "}
                            | {event.startTime} - {event.endTime}
                        </p>
                        <p className="text-sm text-gray-600">
                            {event.trainerNames} · {event.locationName}
                        </p>
                        <div className="mt-4 bg-pink-100 p-3 rounded-lg">
                            <h3 className="font-bold text-gray-800">{product.name}</h3>
                            {productSoldOut && (
                                <div className="mt-1">
                                    <p className="text-sm font-semibold text-gray-700">
                                        {PRODUCT_SOLD_OUT_LABEL} – dieses Produkt kann derzeit nicht gekauft werden.
                                    </p>
                                    {/* Reserved stock from unpaid checkouts is released again, so a
                                        sold out product can become available without anything selling. */}
                                    <button
                                        onClick={() => setReloadCheckout((prev) => prev + 1)}
                                        className="text-pegasusPurple hover:text-pink-700 text-sm underline mt-1"
                                    >
                                        Verfügbarkeit erneut prüfen
                                    </button>
                                </div>
                            )}
                            <p className="text-sm text-gray-600">
                                Aktivierungsdatum:{" "}
                                {new Date(productActivationDate).toLocaleDateString("de-DE", {
                                    day   : "2-digit",
                                    month : "long",
                                    year  : "numeric",
                                })}
                            </p>
                            <p className="text-sm text-gray-600">
                                Ablaufdatum:{" "}
                                {new Date(productExpirationDate).toLocaleDateString("de-DE", {
                                    day   : "2-digit",
                                    month : "long",
                                    year  : "numeric",
                                })}
                            </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            Einmalig: {bruttoPriceWithCoupon} €
                        </p>
                        <p className="text-sm text-gray-600 mt-2 font-bold">
                            Gesamt inkl. MwSt.: {bruttoPriceWithCoupon} €
                        </p>
                        <div className="mt-2">
                            <CouponCodeField
                                activeCouponCode={couponCode}
                                errorMessage={couponErrorMessage}
                                onApply={setCoupon}
                                onRemove={() => setCoupon(null)}
                            />
                        </div>
                    </div>

                    <div className="mt-6 space-y-2">
                        <label className="flex items-start space-x-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={agreeTerms}
                                onChange={() => setAgreeTerms(!agreeTerms)}
                            />
                            <span>
                                Ich akzeptiere die{" "}
                                <a href={`${process.env.REACT_APP_BASE_SITE_URL}/allgemeine-geschaeftsbedingungen`} className="text-pegasusPurple underline">AGB</a>{" "}
                                und die{" "}
                                <a href={`${process.env.REACT_APP_BASE_SITE_URL}/widerrufsbelehrung`} className="text-pegasusPurple underline">Widerrufsbelehrung</a>.
                            </span>
                        </label>
                        <p className="text-sm">
                            Informationen zur Verarbeitung deiner Daten findest du in unserer{" "}
                            <a href={`${process.env.REACT_APP_BASE_SITE_URL}/datenschutzerklaerung`} className="text-pegasusPurple underline">Datenschutzerklärung</a>.
                        </p>
                    </div>

                    <button
                        className={`w-full mt-4 py-2 rounded-lg font-semibold flex items-center justify-center ${
                            isButtonDisabled
                                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                : "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                        }`}
                        disabled={isButtonDisabled}
                        onClick={handlePayment}
                    >
                        {processing && (
                            <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                        )}
                        {processing ? "Wird verarbeitet..." : productSoldOut ? PRODUCT_SOLD_OUT_LABEL.toUpperCase() : "JETZT ZAHLUNGSPFLICHTIG BESTELLEN"}
                    </button>
                </div>
            </div>

            <UnattendedAccessTermsModal
                isOpen={unattendedAccessTerms.isOpen}
                terms={unattendedAccessTerms.terms}
                loading={unattendedAccessTerms.loading}
                accepting={unattendedAccessTerms.accepting}
                error={unattendedAccessTerms.error}
                onAccept={unattendedAccessTerms.acceptTerms}
                onClose={unattendedAccessTerms.closeModal}
            />
        </div>
    );
};

export default CheckoutPage;